import React, { useContext, useEffect, useState } from 'react'
import moment from 'moment'
import { useNavigate } from 'react-router-dom'
import API from '../../../services/API'
import { GlobalContext, TenantSettingsContext } from '../../../App'

import AddIcon from '@mui/icons-material/Add'
import ArchiveIcon from '@mui/icons-material/Archive'
import RefreshIcon from '@mui/icons-material/Refresh'
import UnarchiveIcon from '@mui/icons-material/Unarchive'
import EmailIcon from '@mui/icons-material/Email'

import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import IconButton from '@mui/material/IconButton'
import MenuItem from '@mui/material/MenuItem'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import TextField from '@mui/material/TextField'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'

const emptyForm = {
  firstName: '',
  lastName: '',
  email: '',
  clinicalRole: '',
}

const desktopOnlyCell = { display: { xs: 'none', md: 'table-cell' } }

const getValidityLabel = (subcontractor) =>
  subcontractor.access?.validUntil
    ? moment(subcontractor.access.validUntil).format('DD MMM YYYY HH:mm')
    : subcontractor.access?.validFrom
      ? 'Never expires'
      : 'Awaiting verification'

const statusColor = (subcontractor) => {
  if (subcontractor.status === 'Active') return 'success'
  if (subcontractor.status === 'Pending Verification') return 'warning'
  if (subcontractor.status === 'Archived') return 'default'
  return 'error'
}

const Subcontractors = () => {
  const navigate = useNavigate()
  const { setToast } = useContext(GlobalContext)
  const { orgConfig } = useContext(TenantSettingsContext)
  const roles = orgConfig?.orgSettings?.roles ?? []
  const accessWindowDays =
    orgConfig?.orgSettings?.subcontractorSettings?.accessWindowDays ?? 7
  const accessWindowLabel =
    accessWindowDays === 0
      ? 'indefinite access'
      : `${accessWindowDays} day${accessWindowDays === 1 ? '' : 's'}`

  const [subcontractors, setSubcontractors] = useState([])
  const [loading, setLoading] = useState(false)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [form, setForm] = useState(emptyForm)
  const [error, setError] = useState('')
  const [includeArchived, setIncludeArchived] = useState(false)

  const loadSubcontractors = () => {
    setLoading(true)
    API.get('/subcontractors', {
      params: { includeArchived },
    })
      .then((res) => {
        if (res.status === 200) setSubcontractors(res.data ?? [])
      })
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    loadSubcontractors()
  }, [includeArchived])

  const updateRow = (updated) => {
    setSubcontractors((prev) =>
      prev.map((row) => (row._id === updated._id ? updated : row)),
    )
  }

  const createSubcontractor = () => {
    setError('')
    API.post('/subcontractors', form)
      .then((res) => {
        if (res.status === 200) {
          setSubcontractors((prev) => [res.data, ...prev])
          setDialogOpen(false)
          setForm(emptyForm)
          setToast({ message: 'Subcontractor created' })
        }
      })
      .catch((e) => {
        setError(e?.response?.data?.error || 'Unable to create subcontractor')
      })
  }

  const reactivate = (id) => {
    API.post(`/subcontractors/${id}/reactivate`).then((res) => {
      if (res.status === 200) {
        updateRow(res.data)
        setToast({
          message:
            accessWindowDays === 0
              ? 'Subcontractor reactivated with indefinite access'
              : `Subcontractor reactivated for ${accessWindowLabel}`,
        })
      }
    })
  }

  const archive = (id) => {
    API.post(`/subcontractors/${id}/archive`).then((res) => {
      if (res.status === 200) {
        if (includeArchived) {
          updateRow(res.data)
        } else {
          setSubcontractors((prev) => prev.filter((row) => row._id !== id))
        }
        setToast({ severity: 'info', message: 'Subcontractor archived' })
      }
    })
  }

  const unarchive = (id) => {
    API.post(`/subcontractors/${id}/unarchive`).then((res) => {
      if (res.status === 200) {
        updateRow(res.data)
        setToast({ message: 'Subcontractor restored' })
      }
    })
  }

  const resendVerification = (id) => {
    API.post(`/subcontractors/${id}/resend-verification`).then((res) => {
      if (res.status === 200) {
        updateRow(res.data)
        setToast({ message: 'Verification email sent' })
      }
    })
  }

  return (
    <Stack gap={2}>
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'stretch', md: 'center' }}
        gap={{ xs: 1.5, md: 2 }}
      >
        <Box>
          <Typography variant="title2">Subcontractors</Typography>
          <Typography variant="body2" color="text.secondary">
            {accessWindowDays === 0
              ? 'External clinical accounts with indefinite access - active until revoked or archived.'
              : `External clinical accounts with ${accessWindowLabel} access windows.`}{' '}
            The access window is set in Configuration → Employees →
            Subcontractors.
          </Typography>
        </Box>
        <Stack direction="row" gap={1} alignItems="center" flexShrink={0}>
          <Tooltip
            title={includeArchived ? 'Hide archived' : 'Show archived'}
          >
            <IconButton
              color={includeArchived ? 'primary' : 'default'}
              onClick={() => setIncludeArchived((prev) => !prev)}
            >
              <ArchiveIcon />
            </IconButton>
          </Tooltip>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setDialogOpen(true)}
            sx={{ flex: { xs: 1, md: 'none' }, whiteSpace: 'nowrap' }}
          >
            New Subcontractor
          </Button>
        </Stack>
      </Stack>

      {/* Phones: email/role/validity fold into the Name and Status cells and
          the table scrolls sideways inside the card if it still needs to */}
      <Paper
        variant="outlined"
        sx={{ borderRadius: 2, overflow: 'hidden', overflowX: 'auto' }}
      >
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell sx={desktopOnlyCell}>Email</TableCell>
              <TableCell sx={desktopOnlyCell}>Clinical Role</TableCell>
              <TableCell>Status</TableCell>
              <TableCell sx={desktopOnlyCell}>Valid Until</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {subcontractors.map((subcontractor) => (
              <TableRow
                key={subcontractor._id}
                hover
                onClick={() =>
                  navigate(`/employees/subcontractors/${subcontractor._id}`)
                }
                sx={{ cursor: 'pointer' }}
              >
                <TableCell>
                  {subcontractor.userAccount?.firstName}{' '}
                  {subcontractor.userAccount?.lastName}
                  <Stack sx={{ display: { xs: 'flex', md: 'none' } }}>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ wordBreak: 'break-all' }}
                    >
                      {subcontractor.userAccount?.email}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {subcontractor.clinicalRole}
                    </Typography>
                  </Stack>
                </TableCell>
                <TableCell sx={desktopOnlyCell}>
                  {subcontractor.userAccount?.email}
                </TableCell>
                <TableCell sx={desktopOnlyCell}>
                  {subcontractor.clinicalRole}
                </TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    color={statusColor(subcontractor)}
                    label={subcontractor.status}
                    sx={{ borderRadius: 1 }}
                  />
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: { xs: 'block', md: 'none' }, mt: 0.5 }}
                  >
                    {getValidityLabel(subcontractor)}
                  </Typography>
                </TableCell>
                <TableCell sx={desktopOnlyCell}>
                  {getValidityLabel(subcontractor)}
                </TableCell>
                <TableCell
                  align="right"
                  onClick={(e) => e.stopPropagation()}
                  sx={{ whiteSpace: 'nowrap', pl: { xs: 0.5, md: 2 } }}
                >
                  {subcontractor.status === 'Archived' ? (
                    <Tooltip title="Unarchive">
                      <IconButton onClick={() => unarchive(subcontractor._id)}>
                        <UnarchiveIcon />
                      </IconButton>
                    </Tooltip>
                  ) : (
                    <>
                      {subcontractor.status === 'Pending Verification' && (
                        <Tooltip title="Resend verification email">
                          <IconButton
                            onClick={() =>
                              resendVerification(subcontractor._id)
                            }
                          >
                            <EmailIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                      <Tooltip
                        title={
                          accessWindowDays === 0
                            ? 'Reactivate with indefinite access'
                            : `Reactivate for ${accessWindowLabel}`
                        }
                      >
                        <IconButton onClick={() => reactivate(subcontractor._id)}>
                          <RefreshIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Archive">
                        <IconButton onClick={() => archive(subcontractor._id)}>
                          <ArchiveIcon />
                        </IconButton>
                      </Tooltip>
                    </>
                  )}
                </TableCell>
              </TableRow>
            ))}
            {!loading && subcontractors.length === 0 && (
              <TableRow>
                <TableCell colSpan={6}>
                  <Alert severity="info">No subcontractors found.</Alert>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Paper>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} fullWidth>
        <DialogTitle>New Subcontractor</DialogTitle>
        <DialogContent>
          <Stack gap={2} sx={{ mt: 1 }}>
            {error && <Alert severity="error">{error}</Alert>}
            <Stack direction={{ xs: 'column', sm: 'row' }} gap={1}>
              <TextField
                label="First name"
                value={form.firstName}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, firstName: e.target.value }))
                }
                fullWidth
                required
              />
              <TextField
                label="Last name"
                value={form.lastName}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, lastName: e.target.value }))
                }
                fullWidth
                required
              />
            </Stack>
            <TextField
              label="Email"
              value={form.email}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, email: e.target.value }))
              }
              fullWidth
              required
            />
            <TextField
              select
              label="Clinical role"
              value={form.clinicalRole}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, clinicalRole: e.target.value }))
              }
              fullWidth
              required
            >
              {roles.map((role) => (
                <MenuItem key={role} value={role}>
                  {role}
                </MenuItem>
              ))}
            </TextField>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={
              !form.firstName || !form.lastName || !form.email || !form.clinicalRole
            }
            onClick={createSubcontractor}
          >
            Create
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  )
}

export default Subcontractors
